import type { AttachedFile, DataCategory, Message } from '../types';

export type ClassificationReason = 'message' | 'history' | 'file' | 'unsure';

export interface Classification {
  involves: boolean;
  categories: DataCategory[];
  reason: ClassificationReason | null;
  dataUsed: string | null;
  investorName: string | null;
}

const NAME_PATTERN = /(?<![\p{L}\p{N}])\p{Lu}[\p{Ll}-]+(?:\s+\p{Lu}[\p{Ll}-]+)+/gu;
const NON_ASCII_LETTER = /(?=\p{L})\P{ASCII}/u;

const INVESTOR_WORDS =
  /\b(investors?|clients?|portfolios?|holdings?|positions?|aum|nav|returns?|performance|cash|accounts?|kyc|fees?|allocation|benchmark|wealth|custod(y|ian)|statements?)\b/i;
const CRM_WORDS =
  /\b(tasks?|deals?|pipeline|leads?|crm|contacts?|meetings?|invoices?|requests?|reminders?|follow-?ups?|tickets?)\b/i;
const UNSURE_WORDS = /\b(data|reports?|summar(y|ise|ize)|records?|figures?|numbers?|results?)\b/i;

const CRM_LABELS: [RegExp, string][] = [
  [/\btasks?\b/i, 'Open tasks'],
  [/\bdeals?\b/i, 'Deals'],
  [/\bpipeline\b/i, 'Pipeline'],
  [/\bleads?\b/i, 'Leads'],
  [/\bcontacts?\b/i, 'Contacts'],
  [/\bmeetings?\b/i, 'Meetings'],
  [/\binvoices?\b/i, 'Invoices'],
  [/\brequests?\b/i, 'Client requests'],
];

function matchedNames(text: string): string[] {
  const names = new Set<string>();
  for (const match of text.matchAll(NAME_PATTERN)) names.add(match[0]);
  return [...names];
}

function fromHistory(
  history: Message[],
): { categories: DataCategory[]; dataUsed: string | null } | null {
  for (let i = history.length - 1; i >= 0; i--) {
    const route = history[i]?.route;
    if (route && route.categories.length > 0) {
      return { categories: route.categories, dataUsed: route.dataUsed };
    }
  }
  return null;
}

export function classifyRequest(input: {
  text: string;
  history?: Message[];
  files?: AttachedFile[];
  language?: string;
  carriedInvestorData?: boolean;
}): Classification {
  const text = input.text.trim();
  const names = matchedNames(text);
  const categories: DataCategory[] = [];
  if (INVESTOR_WORDS.test(text)) categories.push('investor');
  if (CRM_WORDS.test(text)) categories.push('crm');

  if (categories.length > 0) {
    const crmLabel = CRM_LABELS.find(([pattern]) => pattern.test(text))?.[1] ?? null;
    const parts = [...(categories.includes('crm') && crmLabel ? [crmLabel] : []), ...names];
    return {
      involves: true,
      categories,
      reason: 'message',
      dataUsed: parts.length > 0 ? parts.join(' · ') : null,
      investorName: names[0] ?? null,
    };
  }

  if (input.files && input.files.length > 0) {
    return {
      involves: true,
      categories: ['investor'],
      reason: 'file',
      dataUsed: input.files.map((f) => f.name).join(' · '),
      investorName: null,
    };
  }

  const earlier =
    fromHistory(input.history ?? []) ??
    (input.carriedInvestorData ? { categories: ['investor' as const], dataUsed: null } : null);
  if (earlier) {
    return {
      involves: true,
      categories: earlier.categories,
      reason: 'history',
      dataUsed: earlier.dataUsed,
      investorName: earlier.dataUsed?.split(' · ')[0] ?? null,
    };
  }

  const unreadable =
    NON_ASCII_LETTER.test(text) ||
    (text.length > 0 && input.language !== undefined && !input.language.startsWith('en'));
  if (names.length > 0 || unreadable || UNSURE_WORDS.test(text)) {
    return {
      involves: true,
      categories: ['investor'],
      reason: 'unsure',
      dataUsed: null,
      investorName: null,
    };
  }

  return { involves: false, categories: [], reason: null, dataUsed: null, investorName: null };
}
